import { SimNPC, getTargetTileForSeeker } from '../types/SimNPC';
import { GameMap, Position, TileType } from '../types/GameMap';
import {
  isValidMove,
  updateNPCPosition,
  getManhattanDistance,
} from './PositionUtils';

/**
 * NPC移動処理
 */
export function moveNPC(npc: SimNPC, map: GameMap, allNPCs: SimNPC[]): void {
  // 属性シーカーかどうかチェック
  const targetTile = getTargetTileForSeeker(npc.movementType);
  if (targetTile) {
    moveAttributeSeeker(npc, map, targetTile as TileType);
    return;
  }

  switch (npc.movementType) {
    case 'random':
      moveRandom(npc, map);
      break;
    case 'explorer':
      moveExplorer(npc, map);
      break;
    case 'social':
      moveSocial(npc, map, allNPCs);
      break;
    case 'avoider':
      moveAvoider(npc, map, allNPCs);
      break;
  }
}

/**
 * 属性シーカー移動（特定属性ゾーンを目指す）
 */
function moveAttributeSeeker(
  npc: SimNPC,
  map: GameMap,
  targetTileType: TileType
): void {
  // 70%の確率で目標タイルを目指す
  if (Math.random() < 0.7) {
    const nearestZone = findNearestTileOfType(npc.position, map, targetTileType);
    if (nearestZone) {
      moveTowards(npc, nearestZone, map);
      return;
    }
  }

  // それ以外はランダム移動
  moveRandom(npc, map);
}

/**
 * ランダム移動
 */
function moveRandom(npc: SimNPC, map: GameMap): void {
  const directions = [
    { dx: 0, dy: -1 }, // 上
    { dx: 0, dy: 1 }, // 下
    { dx: -1, dy: 0 }, // 左
    { dx: 1, dy: 0 }, // 右
  ];

  const dir = directions[Math.floor(Math.random() * 4)];
  const newPos: Position = {
    x: npc.position.x + dir.dx,
    y: npc.position.y + dir.dy,
  };

  if (isValidMove(newPos, map)) {
    updateNPCPosition(npc, newPos, map);
  }
}

/**
 * 探索者移動（町や水場を目指す傾向）
 */
function moveExplorer(npc: SimNPC, map: GameMap): void {
  // 50%の確率で町や水場を目指す
  if (Math.random() < 0.5) {
    // 町を探す
    const nearestTown = findNearestTileOfType(npc.position, map, 'town');
    if (nearestTown) {
      moveTowards(npc, nearestTown, map);
      return;
    }
    // 水場を探す
    const nearestWater = findNearestTileOfType(npc.position, map, 'water');
    if (nearestWater) {
      moveTowards(npc, nearestWater, map);
      return;
    }
  }

  // それ以外はランダム
  moveRandom(npc, map);
}

/**
 * 社交的移動（他NPCに近づく）
 */
function moveSocial(npc: SimNPC, map: GameMap, allNPCs: SimNPC[]): void {
  const nearestNPC = findNearestNPC(npc, allNPCs);
  if (nearestNPC) {
    moveTowards(npc, nearestNPC.position, map);
  } else {
    moveRandom(npc, map);
  }
}

/**
 * 回避移動（他NPCから遠ざかる）
 */
function moveAvoider(npc: SimNPC, map: GameMap, allNPCs: SimNPC[]): void {
  const nearestNPC = findNearestNPC(npc, allNPCs);
  if (nearestNPC) {
    moveAwayFrom(npc, nearestNPC.position, map);
  } else {
    moveRandom(npc, map);
  }
}

/**
 * 目標に向かって1マス移動
 */
function moveTowards(npc: SimNPC, target: Position, map: GameMap): void {
  const dx = target.x - npc.position.x;
  const dy = target.y - npc.position.y;

  // X軸優先で移動
  if (Math.abs(dx) > Math.abs(dy)) {
    const newX = npc.position.x + (dx > 0 ? 1 : -1);
    tryMove(npc, { x: newX, y: npc.position.y }, map);
  } else if (dy !== 0) {
    const newY = npc.position.y + (dy > 0 ? 1 : -1);
    tryMove(npc, { x: npc.position.x, y: newY }, map);
  } else {
    // 同じ位置の場合はランダム
    moveRandom(npc, map);
  }
}

/**
 * 目標から遠ざかる
 */
function moveAwayFrom(npc: SimNPC, target: Position, map: GameMap): void {
  const dx = npc.position.x - target.x;
  const dy = npc.position.y - target.y;

  // 反対方向へ移動
  if (Math.abs(dx) > Math.abs(dy)) {
    const newX = npc.position.x + (dx > 0 ? 1 : -1);
    tryMove(npc, { x: newX, y: npc.position.y }, map);
  } else if (dy !== 0) {
    const newY = npc.position.y + (dy > 0 ? 1 : -1);
    tryMove(npc, { x: npc.position.x, y: newY }, map);
  } else {
    // 同じ位置の場合はランダム
    moveRandom(npc, map);
  }
}

/**
 * 移動試行
 */
function tryMove(npc: SimNPC, newPos: Position, map: GameMap): void {
  if (isValidMove(newPos, map)) {
    updateNPCPosition(npc, newPos, map);
  } else {
    // 移動不可の場合はランダム
    moveRandom(npc, map);
  }
}

/**
 * 最も近いタイルを探す
 */
function findNearestTileOfType(
  from: Position,
  map: GameMap,
  type: TileType
): Position | null {
  let nearest: Position | null = null;
  let minDist = Infinity;

  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (map.tiles[y][x].type === type) {
        const dist = getManhattanDistance(from, { x, y });
        if (dist < minDist) {
          minDist = dist;
          nearest = { x, y };
        }
      }
    }
  }

  return nearest;
}

/**
 * 最も近いNPCを探す
 */
function findNearestNPC(npc: SimNPC, allNPCs: SimNPC[]): SimNPC | null {
  let nearest: SimNPC | null = null;
  let minDist = Infinity;

  for (const other of allNPCs) {
    if (other.id === npc.id || !other.isAlive) continue;

    const dist = getManhattanDistance(npc.position, other.position);
    if (dist < minDist && dist > 0) {
      // dist > 0 で同じ位置を除外
      minDist = dist;
      nearest = other;
    }
  }

  return nearest;
}
